import React from "react";
import { Reveal } from "../components/Reveal";
import { CtaBand } from "./Home";
import { ASSETS } from "../data/content";

const Overline = ({ children }) => (
  <span className="font-mono-el text-xs tracking-[0.25em] uppercase text-[#4FC3F7]">{children}</span>
);

export default function About() {
  return (
    <div data-testid="about-page">
      {/* HERO */}
      <section className="relative pt-40 pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>About EtherLake</Overline>
            <h1 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-light leading-[1.08] tracking-tight text-white max-w-4xl">
              The open medium beneath a{" "}
              <span className="gradient-text font-medium">boundless</span> lakehouse.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-[#94A3B8] max-w-3xl leading-relaxed">
              EtherLake is the company behind Apeiron. We build data infrastructure on the belief that
              the systems connecting and moving an organization's data should be as open, pervasive,
              and invisible as the medium they are named for — present under every workload, every
              engine, and every team, without ever getting in the way.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="relative py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Reveal className="bg-white/5 border border-white/10 p-10 md:p-12">
            <h2 className="font-mono-el text-xs tracking-[0.2em] uppercase text-[#4FC3F7]">Mission</h2>
            <p className="mt-5 text-xl md:text-2xl font-light text-white leading-relaxed">
              To build the open, invisible infrastructure layer that lets data move freely across
              engines, teams, and systems — so organizations spend their effort on insight and action,
              not on plumbing.
            </p>
          </Reveal>
          <Reveal delay={120} className="bg-gradient-to-br from-[#1565C0]/20 to-transparent border border-[#4FC3F7]/20 p-10 md:p-12">
            <h2 className="font-mono-el text-xs tracking-[0.2em] uppercase text-[#4FC3F7]">Vision</h2>
            <p className="mt-5 text-xl md:text-2xl font-light text-white leading-relaxed">
              A future where data infrastructure fades into the background entirely — where AI agents
              and people, technical or not, draw on one open, boundless lakehouse without ever thinking
              about what powers it underneath.
            </p>
          </Reveal>
        </div>
      </section>

      {/* THE NAMES */}
      <section className="relative py-24 md:py-32 bg-[#0A1A2F]/30 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>The Story Behind the Names</Overline>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white max-w-3xl">
              Why EtherLake. Why Apeiron.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
            <Reveal className="border-l-2 border-[#4FC3F7]/40 pl-8">
              <h3 className="text-2xl font-medium text-white">
                Ether <span className="text-[#475569] font-light">+ Lake</span>
              </h3>
              <p className="mt-5 text-[#94A3B8] leading-relaxed text-base md:text-lg">
                “Ether” is the classical name for the invisible medium once thought to fill all space
                and carry light and energy everywhere — the perfect metaphor for infrastructure that
                should be felt everywhere and seen nowhere, open rather than owned by any single party.
                “Lake” grounds that idea in the data lake itself.
              </p>
            </Reveal>
            <Reveal delay={120} className="border-l-2 border-[#4FC3F7]/40 pl-8">
              <h3 className="text-2xl font-medium text-white">
                Apeiron <span className="text-[#475569] font-light">· ἄπειρον</span>
              </h3>
              <p className="mt-5 text-[#94A3B8] leading-relaxed text-base md:text-lg">
                “Apeiron” is ancient Greek for “boundless” or “limitless,” naming the product's promise
                directly: unconstrained scale, open formats, and freedom from lock-in. Together,
                EtherLake builds the open medium beneath a boundless lakehouse — the ether under
                everything, with no ceiling above it.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* IMAGE / PARTNER */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal className="relative">
            <div className="absolute -inset-4 bg-[#4FC3F7]/10 blur-3xl" />
            <img
              src={ASSETS.aboutTeam}
              alt="EtherLake team collaborating"
              className="relative w-full h-[420px] object-cover border border-white/10"
            />
          </Reveal>
          <Reveal delay={120}>
            <Overline>Design Partners</Overline>
            <h2 className="mt-5 text-3xl md:text-4xl font-light tracking-tight text-white">
              We're building Apeiron with our earliest users.
            </h2>
            <p className="mt-6 text-[#94A3B8] text-lg leading-relaxed">
              Apeiron, by EtherLake, is currently in early development as a working prototype. We're
              looking for design partners to help shape the product ahead of broader availability — if
              your team is fighting warehouse cost, complexity, or shallow AI, we'd love to talk.
            </p>
            <a
              href="/contact"
              data-testid="about-partner-cta"
              className="mt-8 inline-flex items-center gap-2 bg-[#4FC3F7] text-[#020817] hover:bg-[#00E5FF] transition-colors duration-300 px-8 py-4 font-semibold"
            >
              Become a design partner
            </a>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
